import React, { useState } from "react";
import { X, Upload, Target, Trash2, Image as ImageIcon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { REPORT_MODAL } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyState = {
  name: "",
  email: "",
  phone: "",
  address: "",
  listingAgent: "",
  signInFile: null, // { filename, dataUrl, base64, sizeKb }
  socialFile: null,
  contactedPrior: "", // "yes" | "no"
  enrolledInProgram: "", // "yes" | "no" | ""
};

// Resize image client-side and return base64 (JPEG). Keeps email payload small.
const readAndResize = (file, maxDim = 1600, quality = 0.85) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        const base64 = dataUrl.split(",")[1];
        const sizeKb = Math.round((base64.length * 3) / 4 / 1024);
        resolve({
          filename: file.name.replace(/\.[^.]+$/, "") + ".jpg",
          dataUrl,
          base64,
          sizeKb,
        });
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const ReportActivityModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(emptyState);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (key, file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Only image files can be uploaded");
      return;
    }
    try {
      const processed = await readAndResize(file);
      set(key, processed);
    } catch {
      toast.error("Could not read that image");
    }
  };

  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.address.trim() &&
    form.contactedPrior;

  const resetAndClose = () => {
    setForm(emptyState);
    onOpenChange(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      address: form.address.trim(),
      listing_agent: form.listingAgent.trim() || null,
      contacted_listing_agent_prior: form.contactedPrior,
      enrolled_in_program: form.enrolledInProgram || null,
      sign_in_sheet: form.signInFile
        ? { filename: form.signInFile.filename, base64: form.signInFile.base64 }
        : null,
      social_media: form.socialFile
        ? { filename: form.socialFile.filename, base64: form.socialFile.base64 }
        : null,
    };

    try {
      const resp = await fetch(`${API}/submit-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast.success("Report submitted", {
        description: "Your open house activity has been sent to KW Pro Coach.",
      });
      resetAndClose();
    } catch (err) {
      toast.error("Couldn't submit report", {
        description: "Please try again. If the issue persists, email kwwprocoach@kw.com directly.",
      });
      console.error("submit-report failed:", err?.message || err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={REPORT_MODAL.root}
        className="max-w-3xl p-0 gap-0 max-h-[92vh] overflow-hidden bg-white border-0 rounded-3xl shadow-2xl [&>button]:hidden"
      >
        <div className="kw-red-bar h-1 w-full" />

        <DialogHeader className="px-8 py-5 border-b border-[color:var(--kw-border)] flex-row items-center justify-between space-y-0">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--kw-red)] mb-1">
              KW · Agent Success Coaching
            </div>
            <DialogTitle className="font-display text-2xl md:text-3xl font-black tracking-tight text-[color:var(--kw-charcoal)] uppercase">
              Open House Activity Tracker
            </DialogTitle>
          </div>
          <DialogClose
            data-testid={REPORT_MODAL.closeBtn}
            className="text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)] transition-colors"
          >
            <X size={22} strokeWidth={2.5} />
          </DialogClose>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="overflow-y-auto kw-scroll px-8 py-6 space-y-6"
          style={{ maxHeight: "calc(92vh - 96px)" }}
        >
          {/* Intro */}
          <div className="rounded-2xl bg-[color:var(--kw-cream)] border border-[color:var(--kw-border)] px-5 py-4 text-sm text-[color:var(--kw-charcoal-soft)] leading-relaxed">
            <p>Please use the form below to log your open house activity.</p>
            <p className="mt-2">
              As a reminder, under our current Agent Success program, PC agents who host <span className="font-bold text-[color:var(--kw-charcoal)]">4+ open houses per month</span> are qualified to have their monthly fees waived.
            </p>
            <p className="mt-2">
              For any questions, please email{" "}
              <a href="mailto:kwwprocoach@kw.com" className="font-bold text-[color:var(--kw-red)] hover:underline">
                kwwprocoach@kw.com
              </a>
              .
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="label-caps block mb-1.5">Name *</label>
            <input
              data-testid={REPORT_MODAL.fieldName}
              className="input-kw"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
            />
          </div>

          {/* Email + Phone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="label-caps block mb-1.5">Email *</label>
              <input
                data-testid={REPORT_MODAL.fieldEmail}
                type="email"
                className="input-kw"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label-caps block mb-1.5">Phone Number *</label>
              <input
                data-testid={REPORT_MODAL.fieldPhone}
                type="tel"
                className="input-kw"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                required
              />
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="label-caps block mb-1.5">Address of Open House *</label>
            <textarea
              data-testid={REPORT_MODAL.fieldAddress}
              className="input-kw min-h-[70px] resize-y"
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              required
            />
          </div>

          {/* Listing Agent Name */}
          <div>
            <label className="label-caps block mb-1.5">Listing Agent Name</label>
            <input
              data-testid={REPORT_MODAL.fieldListingAgent}
              className="input-kw"
              value={form.listingAgent}
              onChange={(e) => set("listingAgent", e.target.value)}
            />
          </div>

          {/* File Uploads */}
          <FileField
            testId={REPORT_MODAL.fileSignIn}
            label="Upload Sign In Sheet from the Open House"
            hint="1 photo only"
            file={form.signInFile}
            onFile={(f) => handleFile("signInFile", f)}
            onClear={() => set("signInFile", null)}
          />

          <FileField
            testId={REPORT_MODAL.fileSocial}
            label="Upload ONE screenshot of social media where you promoted this Open House"
            hint="Must be a live social media post including the day and time of your Open House. (Uploading a flyer here will not suffice.)"
            file={form.socialFile}
            onFile={(f) => handleFile("socialFile", f)}
            onClear={() => set("socialFile", null)}
          />

          {/* Contact Listing Agent Prior */}
          <div>
            <div className="label-caps block mb-2">
              Did you contact the listing agent for information prior to holding the Open House? *
            </div>
            <div className="flex gap-3">
              <RadioPill
                testId={REPORT_MODAL.contactYes}
                label="Yes"
                selected={form.contactedPrior === "yes"}
                onSelect={() => set("contactedPrior", "yes")}
              />
              <RadioPill
                testId={REPORT_MODAL.contactNo}
                label="No"
                selected={form.contactedPrior === "no"}
                onSelect={() => set("contactedPrior", "no")}
              />
            </div>
          </div>

          {/* Enrolled in Agent Success */}
          <div>
            <div className="label-caps block mb-2">
              Are you currently enrolled in the Agent Success Program?
            </div>
            <div className="flex gap-3">
              <RadioPill
                testId={REPORT_MODAL.enrolledYes}
                label="Yes"
                selected={form.enrolledInProgram === "yes"}
                onSelect={() => set("enrolledInProgram", "yes")}
              />
              <RadioPill
                testId={REPORT_MODAL.enrolledNo}
                label="No"
                selected={form.enrolledInProgram === "no"}
                onSelect={() => set("enrolledInProgram", "no")}
              />
            </div>
          </div>

          {/* Why it matters */}
          <div className="rounded-2xl border-l-4 border-[color:var(--kw-red)] bg-[color:var(--kw-cream)] px-5 py-4">
            <div className="flex items-center gap-2 mb-1.5">
              <Target size={16} className="text-[color:var(--kw-red)]" strokeWidth={2.5} />
              <div className="font-black uppercase tracking-widest text-xs text-[color:var(--kw-red)]">
                Why it matters
              </div>
            </div>
            <p className="text-sm text-[color:var(--kw-charcoal-soft)] leading-relaxed">
              Hosting open houses is the ultimate free way for agents to get face-to-face with active buyers and neighborhood homeowners. By positioning yourself as the local expert, you let warm leads walk right through your door. It&rsquo;s the perfect low-pressure environment to build instant rapport and quickly grow your database.
            </p>
          </div>

          {/* Submit */}
          <div className="pt-1 pb-1">
            <button
              data-testid={REPORT_MODAL.submitBtn}
              type="submit"
              disabled={!canSubmit || submitting}
              className={`w-full py-4 rounded-full text-base kw-btn-primary ${(!canSubmit || submitting) ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {submitting ? "Submitting…" : "Submit Report"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const RadioPill = ({ testId, label, selected, onSelect }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onSelect}
    className={`px-6 py-2.5 rounded-full text-sm font-black tracking-widest uppercase transition-all border-2 ${
      selected
        ? "bg-[color:var(--kw-red)] text-white border-[color:var(--kw-red)] shadow-[0_6px_18px_-8px_rgba(209,0,0,0.5)]"
        : "bg-white text-[color:var(--kw-charcoal-soft)] border-[color:var(--kw-border)] hover:border-[color:var(--kw-charcoal)]"
    }`}
  >
    {label}
  </button>
);

const FileField = ({ testId, label, hint, file, onFile, onClear }) => {
  const inputId = `${testId}-input`;
  return (
    <div>
      <label htmlFor={inputId} className="label-caps block mb-2 cursor-pointer">
        {label}
      </label>
      {hint && (
        <p className="text-xs text-[color:var(--muted-foreground)] mb-3 leading-snug">{hint}</p>
      )}

      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-[color:var(--kw-border)] bg-white p-3">
          <img
            src={file.dataUrl}
            alt={file.filename}
            className="w-16 h-16 object-cover rounded-lg border border-[color:var(--kw-border)]"
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-[color:var(--kw-charcoal)] truncate">{file.filename}</div>
            <div className="text-xs text-[color:var(--muted-foreground)]">{file.sizeKb} KB</div>
          </div>
          <button
            type="button"
            onClick={onClear}
            className="p-2 text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-red)] transition-colors"
            aria-label="Remove file"
          >
            <Trash2 size={16} strokeWidth={2.2} />
          </button>
        </div>
      ) : (
        <label
          htmlFor={inputId}
          className="flex items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-[color:var(--kw-border)] hover:border-[color:var(--kw-red)] bg-[color:var(--kw-cream)]/40 hover:bg-[color:var(--kw-cream)] transition-all px-4 py-5 cursor-pointer group"
        >
          <div className="w-9 h-9 rounded-full bg-white border border-[color:var(--kw-border)] flex items-center justify-center group-hover:border-[color:var(--kw-red)] group-hover:bg-[color:var(--kw-red)]/5 transition-colors">
            <ImageIcon size={16} className="text-[color:var(--kw-charcoal-soft)] group-hover:text-[color:var(--kw-red)]" strokeWidth={2.2} />
          </div>
          <div className="text-sm">
            <span className="font-bold text-[color:var(--kw-charcoal)]">Choose image</span>
            <span className="text-[color:var(--muted-foreground)]"> · PNG or JPG</span>
          </div>
        </label>
      )}

      <input
        id={inputId}
        data-testid={testId}
        type="file"
        accept="image/*"
        onChange={(e) => onFile(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
};

export default ReportActivityModal;
